/**
 * 后台上传 worker —— 把 uploadQueue 里排队的原图补传出去，2026-10-20。
 *
 * 需求来源（用户）：「后台上传应该退出页面也能继续…只要不关闭项目吧…」
 *
 * 为什么是「全局单例 + 串行」而不是每个上传各自并发：
 *   ① **退出页面继续**：worker 挂在模块级（`startUploadRunner()` 在 App 启动时调用一次），
 *      不随任何组件卸载而停止。用户在「现场照片」点上传后切到别的菜单 / 别的项目，
 *      补传照样在跑 —— 这就是用户要的那条「只要不关闭页面」。
 *   ② **串行不抢带宽**：手机一次选 10 张照片，如果 10 份原图同时补传，会把用户正在用的
 *      页面请求（列表、图片预览）一起拖慢，弱网下体感极差。一次只跑一份，跑完再下一份。
 *   ③ **刷新也能续**：任务在 IndexedDB 里，刷新后 worker 重新扫库接着传（分片上传本身
 *      还有断点续传，已传的分片不会重传）。
 *
 * 失败策略：
 *   网络类错误 → 指数退避重试，最多 MAX_TRIES 次；
 *   4xx（文件不存在 / 无权限 / 校验失败）→ 立刻放弃并丢弃，重试没有意义；
 *   超限丢弃后**不打扰用户** —— 原图补传是附带能力，预览图一直是可用的，
 *   为它弹一个「你的原图没传上去」只会制造焦虑。调试时看 console.warn 即可。
 */
import api from "./api";
import { peekOriginal, removeOriginal, bumpOriginalTries, setEnqueueHook } from "./uploadQueue";
import { uploadChunked } from "./upload";

const MAX_TRIES = 3;            // 单份原图最多尝试次数
const RETRY_BASE_MS = 4000;     // 退避基数：4s → 8s → 16s
const POLL_MS = 15000;          // 空闲时每隔多久扫一次库（兜底：入队事件丢失时也能捞到）

let running = false;
let started = false;
let wakeUp = null;

/**
 * 当前这个模块实例的「主人标记」。
 *
 * ⚠️ 为什么要它（2026-10-20 浏览器实测踩到）：开发环境 CRA 热更新会**重新执行整个模块**，
 * 旧的 `loop()` 还在跑（它 `running=true`、卡在自己的 await 里，永远不会退出），
 * 新的模块实例又起一个新的 loop —— 于是同一个任务被 2~3 个 worker 同时上传。
 * 后端日志里能看到同一秒内三组 `uploads/chunk` / `finalize` / `/original` 打同一个 previewFileId，
 * 既浪费带宽又可能让 `/original` 的幂等写入互相覆盖。
 * 修法：每个模块实例领一个 id，写进 window 当「当前主人」；每轮循环先确认自己还是主人，
 * 不是就安静退出。生产环境模块只加载一次，这段逻辑恒等于「一直是主人」，无额外开销。
 */
const ownerId = `runner_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// 可观测状态：排查「上传队列不动」时不必再猜（window.__uploadRunner）。
// 生产环境保留成本极低（几个字段），换来的是线上可以直接问用户「在控制台敲
// __uploadRunner 看一下」就能定位是没启动、卡住、还是在反复失败。
const stats = { started: false, running: false, lastPeek: null, lastError: null, done: 0, failed: 0 };
if (typeof window !== "undefined") {
  try {
    // 接管所有权：旧实例的循环会在下一轮检查时发现已经不是主人，自行退出
    window.__uploadRunnerOwner = ownerId;
    window.__uploadRunner = stats;
  } catch { /* */ }
}
const isOwner = () => {
  try { return typeof window === "undefined" || !window.__uploadRunnerOwner || window.__uploadRunnerOwner === ownerId; } catch { return true; }
};

const log = (...a) => { try { console.warn("[uploadRunner]", ...a); } catch { /* */ } };

/** 叫醒空闲中的 worker（入队后调用，避免等到下一个轮询周期）。 */
export function kickUploadRunner() {
  const fn = wakeUp;
  wakeUp = null;
  try { fn?.(); } catch { /* */ }
}

/**
 * 可被 kick 提前唤醒的等待。
 *
 * ⚠️ 这里踩过一个真 bug（2026-10-20 浏览器实测）：最初的实现是
 *   `new Promise((res) => { wakeUp = res; setTimeout(res, ms); })`
 * 两个问题：
 *   ① 超时 resolve 之后 `wakeUp` **仍然指着这个已经用完的 resolve**，
 *      下一次 kick 调到的是上一轮的残骸，当前这一轮根本醒不了；
 *   ② resolve 幂等，但 `wakeUp` 不清空会让状态一直错位 —— 表现就是
 *      `window.__uploadRunner` 里 `running:true` 但 `lastPeek` 永不更新，
 *      队列里的任务一直躺着没人处理（实测卡了 10 秒以上才发现）。
 * 现在：谁先到（kick 或超时）都统一 `settle()`，并且**清空两个句柄**，
 * 保证每轮等待只被消费一次、不会污染下一轮。
 */
const sleep = (ms) => new Promise((res) => {
  let t = null;
  const settle = () => {
    if (t) { clearTimeout(t); t = null; }
    if (wakeUp === settle) wakeUp = null;
    res();
  };
  wakeUp = settle;
  t = setTimeout(settle, ms);
});

const isRetriable = (e) => {
  const st = e?.response?.status;
  // 4xx 里除了 408/429 都是「这份文件没救了」，别浪费时间重试
  if (st && st >= 400 && st < 500 && st !== 408 && st !== 429) return false;
  return true;
};

/**
 * 处理一条任务。
 * 复用 uploadChunked（skipCompress=true）上传源文件，再把它挂到预览文件上。
 * ⚠️ signal 传 undefined：**不能**让任何组件的 AbortController 掐断它 ——
 *    这正是"退出页面就中断"的根因，见 uploadQueue.js 头部说明。
 */
async function runOne(task) {
  // ⚠️ lastModified 必须沿用入队时记下的值，不能写 Date.now()。
  // 断点续传的 localStorage key 是 `名字:大小:lastModified`（见 upload.js 的 fileKey），
  // 每次重试都换一个时间戳 = 每次都当成"新文件"从第 0 片重传，续传形同虚设。
  const file = new File([task.blob], task.filename, { type: task.contentType, lastModified: task.lastModified || Date.now() });
  const fin = await uploadChunked(api, file, {
    shrink: false, skipCompress: true,
    onStage: (s) => { stats.lastStage = s; },
  });
  stats.lastStage = "linked:uploading";
  await api.post(`/files/${task.previewFileId}/original`, { originalFileId: fin.fileId });
  stats.lastStage = "linked";
  return fin;
}

/**
 * 当前登录用户（worker 只补传他自己的任务，见 uploadQueue.peekOriginal 的说明）。
 * 从 ali_user 读而不是从 api 的 Authorization 解 JWT：这里是浏览器侧、拿到的就是
 * 登录时存下的同一个对象，不需要再引入 jwt 解析依赖。
 */
const currentUser = () => {
  try { return JSON.parse(localStorage.getItem("ali_user") || "{}").username || ""; } catch { return ""; }
};

/** 主循环：串行取任务 → 跑 → 删；空库就睡一会儿再看。 */
async function loop() {
  if (running) return;
  running = true;
  stats.running = true;
  let turn = 0;
  try {
    for (;;) {
      if (!isOwner()) { log("已有更新的 worker 实例接管，本实例退出"); return; }
      turn += 1;
      stats.turn = turn;
      let task = null;
      try { task = await peekOriginal(currentUser()); stats.lastPeek = Date.now(); } catch (e) { task = null; stats.lastError = "peek: " + String(e?.message || e); }
      stats.lastTaskId = task ? task.id : null;
      if (!task) { await sleep(POLL_MS); continue; }
      const tries = (task.tries || 0) + 1;
      try {
        await runOne(task);
        await removeOriginal(task.id);
        stats.done += 1;
      } catch (e) {
        const retriable = isRetriable(e);
        stats.lastError = "run: " + String(e?.message || e);
        stats.lastStage = "error";
        log("补传失败", task.id, `第 ${tries} 次`, retriable ? "将重试" : "不再重试", e?.message || e);
        if (!retriable || tries >= MAX_TRIES) {
          // 放弃：删掉任务，避免同一份坏文件无限重试占着队列
          try { await removeOriginal(task.id); } catch { /* */ }
          stats.failed += 1;
        } else {
          try { await bumpOriginalTries(task.id, tries); } catch { /* */ }
          await sleep(RETRY_BASE_MS * 2 ** (tries - 1));
        }
      }
    }
  } catch (e) {
    // 循环整体崩掉（例如某个 await 抛了非预期异常）：记下来，别让 running 永久为 true
    stats.lastError = "loop: " + String(e?.message || e);
    log("worker 循环异常退出", e);
  } finally {
    running = false;
    stats.running = false;
  }
}

/**
 * 启动 worker。在 App 挂载时调用一次即可（重复调用是幂等的）。
 *
 * 只在已登录时启动：没 token 时上传必然 401，跑起来只会刷一片失败日志，
 * 还会把任务一次次消耗到 MAX_TRIES。未登录时什么都不做，登录后会由 App 再次调用。
 *
 * ⚠️ 踩过的坑（2026-10-20 实测）：原来先 `started = true` 再启动循环。
 *    一旦启动过程中抛了异常（当时是引用了已删掉的函数名），`started` 已经是 true，
 *    之后再调用就直接 return —— 循环永远起不来，而 `window.__uploadRunner` 显示
 *    `started: true, running: false`，看起来"启动了但没跑"，极难对账。
 *    现在改成：**先把循环真正拉起来，再标记 started**，且启动异常直接抛给调用方
 *    （App 的 effect 里会出现在 console），不会被静默吞掉。
 */
export function startUploadRunner() {
  if (started) return;
  if (typeof window === "undefined") return;
  if (!api.defaults.headers.common?.["Authorization"]) return;   // 未登录
  // 入队后立刻叫醒（见 uploadQueue.setEnqueueHook 的说明：避免循环依赖）
  setEnqueueHook(() => kickUploadRunner());
  // 网络恢复时立刻补一把（断网期间任务会卡在重试退避里，恢复后没必要等满退避）
  window.addEventListener("online", () => kickUploadRunner());
  started = true;
  stats.started = true;
  loop();
}
